import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { 
  Settings, CreditCard, CloudUpload, GraduationCap, ArrowLeft, ExternalLink, 
  DollarSign, Database, Bell, AlertTriangle, CheckCircle, Clock, RefreshCw,
  Users, Calendar, FileText, Upload, Trash2, Download, Shield, MessageCircle
} from 'lucide-react';
import CompanyTrainingManager from '@/components/CompanyTrainingManager';
import CompanyTimezoneSetting from '@/components/CompanyTimezoneSetting';
import CorrectiveActionDigestSetting from '@/components/CorrectiveActionDigestSetting';
import useFeatureFlags from '@/hooks/useFeatureFlags';

const AdminSettings = ({ user, setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const flags = useFeatureFlags();
  // Billing removed - going different route
  const [stats, setStats] = useState({ totalUsers: 0, activeWorkers: 0, certificates: 0, completions: 0 });
  const [cloudStorageConfig, setCloudStorageConfig] = useState(null);
  const [showCloudDialog, setShowCloudDialog] = useState(false);
  const [cloudForm, setCloudForm] = useState({
    provider: 'r2',
    accountId: '',
    accessKeyId: '',
    secretAccessKey: '',
    bucketName: ''
  });
  
  // Safety Manual state
  const [safetyManual, setSafetyManual] = useState(null);
  const [uploadingManual, setUploadingManual] = useState(false);
  
  // Notification system state
  const [expiringItems, setExpiringItems] = useState({ drugScreens: null, training: null });
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [triggeringNotifications, setTriggeringNotifications] = useState(false);
  const [notificationResult, setNotificationResult] = useState(null);

  const loadBillingInfo = async () => {
    try {
      const response = await axiosInstance.get('/admin/company-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadCloudStorage = async () => {
    try {
      const response = await axiosInstance.get('/admin/cloud-storage');
      setCloudStorageConfig(response.data);
    } catch (error) {
      console.error('Failed to load cloud storage config:', error);
    }
  };
  
  const loadSafetyManual = async () => {
    try {
      const response = await axiosInstance.get('/company/safety-manual');
      setSafetyManual(response.data);
    } catch (error) {
      console.error('Failed to load safety manual:', error);
    }
  };

  useEffect(() => {
    loadBillingInfo();
    loadCloudStorage();
    loadExpiringItems();
    loadSafetyManual();
  }, []);

  // Load expiring drug screens and training
  const loadExpiringItems = async () => {
    setLoadingNotifications(true);
    try {
      // Load drug screen expirations
      const drugResponse = await axiosInstance.get('/admin/users/drug-screens/expiring?days=30');
      
      // Count training expirations (would need a new endpoint or use existing course completions)
      setExpiringItems({
        drugScreens: drugResponse.data,
        training: null // TODO: Add training expiration endpoint
      });
    } catch (error) {
      console.error('Failed to load expiring items:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Trigger notification emails manually
  const handleTriggerNotifications = async () => {
    setTriggeringNotifications(true);
    setNotificationResult(null);
    try {
      const response = await axiosInstance.post('/admin/notifications/test-expiration-emails');
      setNotificationResult(response.data);
      toast.success(t('adminToasts.NotificationCheckCompleted'));
      loadExpiringItems(); // Refresh the data
    } catch (error) {
      console.error('Failed to trigger notifications:', error);
      toast.error(t('adminToasts.FailedToTriggerNotifications'));
      setNotificationResult({ success: false, message: error.message });
    } finally {
      setTriggeringNotifications(false);
    }
  };

  // Safety Manual Upload Handler
  const handleSafetyManualUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error(t('adminToasts.PleaseUploadAPdfFile'));
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('adminToasts.FileSizeMustBeLessThanMb'));
      return;
    }
    
    setUploadingManual(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result.split(',')[1];
        
        // Extract text from PDF (basic extraction)
        let extractedText = '';
        try {
          // Use pdfjsLib if available, otherwise skip extraction
          if (window.pdfjsLib) {
            const pdf = await window.pdfjsLib.getDocument({ data: atob(base64Data) }).promise;
            for (let i = 1; i <= Math.min(pdf.numPages, 50); i++) {
              const page = await pdf.getPage(i);
              const textContent = await page.getTextContent();
              extractedText += textContent.items.map(item => item.str).join(' ') + '\n';
            }
          }
        } catch (extractErr) {
          console.log('PDF text extraction skipped:', extractErr);
        }
        
        // Upload to backend
        const response = await axiosInstance.post('/company/safety-manual', {
          fileName: file.name,
          fileData: base64Data,
          extractedText: extractedText
        });
        
        toast.success(t('adminToasts.SafetyManualUploadedSuccessfully'));
        loadSafetyManual();
      };
      
      reader.onerror = () => {
        toast.error(t('adminToasts.FailedToReadFile'));
        setUploadingManual(false);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to upload safety manual:', error);
      toast.error(t('adminToasts.FailedToUploadSafetyManual'));
    } finally {
      setUploadingManual(false);
    }
  };
  
  const handleDeleteSafetyManual = async () => {
    if (!confirm('Are you sure you want to delete the company safety manual?')) return;
    
    try {
      await axiosInstance.delete('/company/safety-manual');
      toast.success(t('adminToasts.SafetyManualDeleted'));
      setSafetyManual(null);
    } catch (error) {
      console.error('Failed to delete safety manual:', error);
      toast.error(t('adminToasts.FailedToDeleteSafetyManual'));
    }
  };
  
  const handleDownloadSafetyManual = () => {
    if (!safetyManual?.fileData) return;
    
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${safetyManual.fileData}`;
    link.download = safetyManual.fileName || 'safety-manual.pdf';
    link.click();
  };

  // Billing functions removed

  const handleSaveCloudStorage = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/admin/cloud-storage', cloudForm);
      toast.success(t('adminToasts.CloudStorageConfiguredSuccessfully'));
      setShowCloudDialog(false);
      loadCloudStorage();
    } catch (error) {
      console.error('Failed to configure cloud storage:', error);
      toast.error(t('adminToasts.FailedToConfigureCloudStorage'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('adminUI.CompanySettings')}</h1>
          <p className="text-gray-600 mt-1">{t('adminUI.ManageCloudStorageAndCompanyConfigurations')}</p>
        </div>

        {/* AI Safety Assistant Quick Access */}
        <Card className="mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('adminUI.AiSafetyAssistant')}</h3>
                  <p className="text-sm text-gray-600">{t('adminUI.AskSafetyQuestionsAndGetInstantAnswersFromYourManuals')}</p>
                </div>
              </div>
              <Button onClick={() => navigate('/safety-assistant')} className="gap-2">
                <Shield className="w-4 h-4" />
                Open Assistant
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Settings Grid */}
        <div className="grid md:grid-cols-1 gap-6">
          {/* Reporting timezone - drives the 7/30/90-day dashboard windows */}
          <CompanyTimezoneSetting />
          {flags.phase3CorrectiveActions && <CorrectiveActionDigestSetting />}

          {/* Company Safety Manual */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Company Safety Manual
              </CardTitle>
              <CardDescription>{t('adminUI.UploadYourCompanyWideSafetyManualForAiPoweredQA')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {safetyManual?.hasManual ? (
                <>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-blue-600" />
                        <div>
                          <div className="font-medium text-gray-900">{safetyManual.fileName}</div>
                          <div className="text-xs text-gray-500">
                            Uploaded {safetyManual.uploadedAt ? new Date(safetyManual.uploadedAt).toLocaleDateString() : 'recently'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={handleDownloadSafetyManual}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleDeleteSafetyManual} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-600 font-medium">{t('adminUI.ManualUploadedAiAssistantCanSearchThisDocument')}</span>
                  </div>
                </>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-600 text-sm">{t('adminUI.NoCompanySafetyManualUploaded')}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('adminUI.UploadAPdfToEnableAiPoweredSafetyQA')}</p>
                </div>
              )}
              <div>
                <input
                  type="file"
                  id="safety-manual-upload"
                  accept="application/pdf"
                  onChange={handleSafetyManualUpload}
                  className="hidden"
                />
                <Button
                  onClick={() => document.getElementById('safety-manual-upload').click()}
                  disabled={uploadingManual}
                  variant={safetyManual?.hasManual ? 'outline' : 'default'}
                  className="w-full gap-2"
                >
                  {uploadingManual ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      {safetyManual?.hasManual ? 'Replace Manual' : 'Upload Safety Manual (PDF)'}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Billing & Subscription - REMOVED */}

          {/* Cloud Storage */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CloudUpload className="w-5 h-5 text-purple-600" />
                Cloud Storage
              </CardTitle>
              <CardDescription>{t('adminUI.ConfigurePersistentFileStorage')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {cloudStorageConfig && cloudStorageConfig.configured ? (
                <>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 mb-1">Provider</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {cloudStorageConfig.provider === 'r2' ? 'Cloudflare R2' : cloudStorageConfig.provider}
                    </div>
                    <div className="text-sm text-gray-600 mt-2">
                      Bucket: {cloudStorageConfig.bucketName}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-green-600 font-medium">{t('adminUI.Connected')}</span>
                  </div>
                </>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <CloudUpload className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-600 text-sm">{t('adminUI.NoCloudStorageConfigured')}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('adminUI.FilesAreStoredLocallyByDefault')}</p>
                </div>
              )}
              <Button
                onClick={() => setShowCloudDialog(true)}
                variant={cloudStorageConfig?.configured ? 'outline' : 'default'}
                className="w-full gap-2"
              >
                <CloudUpload className="w-4 h-4" />
                {cloudStorageConfig?.configured ? 'Reconfigure' : 'Configure Storage'}
              </Button>
            </CardContent>
          </Card>

          {/* Required Courses */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-indigo-600" />
                Required Courses
              </CardTitle>
              <CardDescription>{t('adminUI.ConfigureMandatoryTraining')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Set which training courses are required for all workers in your company. Workers must complete these courses for compliance.
              </p>
              <Button
                onClick={() => navigate('/admin/training')}
                variant="outline"
                className="w-full gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Configure Required Courses
              </Button>
            </CardContent>
          </Card>

          {/* Expiration Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-600" />
                Expiration Notifications
              </CardTitle>
              <CardDescription>{t('adminUI.ManageAutomatedEmailRemindersForExpiringDrugScreensAndTraini')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingNotifications ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                  <span className="text-gray-500">{t('adminUI.Loading')}</span>
                </div>
              ) : (
                <>
                  {/* Drug Screen Summary */}
                  {expiringItems.drugScreens && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Drug Screen Status (Next 30 Days)
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-red-50 p-3 rounded-lg text-center">
                          <div className="text-red-600 font-bold text-xl">
                            {expiringItems.drugScreens.totalExpired || 0}
                          </div>
                          <div className="text-xs text-red-600">Expired</div>
                        </div>
                        <div className="bg-amber-50 p-3 rounded-lg text-center">
                          <div className="text-amber-600 font-bold text-xl">
                            {expiringItems.drugScreens.totalExpiringSoon || 0}
                          </div>
                          <div className="text-xs text-amber-600">Expiring Soon</div>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg text-center">
                          <div className="text-green-600 font-bold text-xl">
                            {expiringItems.drugScreens.totalValid || 0}
                          </div>
                          <div className="text-xs text-green-600">Valid</div>
                        </div>
                      </div>
                      
                      {/* Expiring Workers List */}
                      {(expiringItems.drugScreens.expired?.length > 0 || expiringItems.drugScreens.expiringSoon?.length > 0) && (
                        <div className="mt-3 max-h-40 overflow-y-auto">
                          {expiringItems.drugScreens.expired?.map((worker) => (
                            <div key={worker.id} className="flex items-center justify-between py-2 border-b text-sm">
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                                <span className="font-medium">{worker.name}</span>
                              </div>
                              <span className="text-red-500 text-xs">
                                Expired {Math.abs(worker.daysUntilExpiration)} days ago
                              </span>
                            </div>
                          ))}
                          {expiringItems.drugScreens.expiringSoon?.map((worker) => (
                            <div key={worker.id} className="flex items-center justify-between py-2 border-b text-sm">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-amber-500" />
                                <span className="font-medium">{worker.name}</span>
                              </div>
                              <span className="text-amber-500 text-xs">
                                {worker.daysUntilExpiration} days left
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notification Actions */}
                  <div className="pt-3 border-t space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Emails sent automatically at: 30, 14, 7, 3, and 1 day(s) before expiration
                      </div>
                    </div>
                    
                    <Button
                      onClick={handleTriggerNotifications}
                      disabled={triggeringNotifications}
                      className="w-full gap-2"
                      variant="outline"
                    >
                      {triggeringNotifications ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Sending Notifications...
                        </>
                      ) : (
                        <>
                          <Bell className="w-4 h-4" />
                          Send Notification Reminders Now
                        </>
                      )}
                    </Button>

                    {/* Result display */}
                    {notificationResult && (
                      <div className={`p-3 rounded-lg text-sm ${notificationResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {notificationResult.success ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <AlertTriangle className="w-4 h-4" />
                          )}
                          <span className="font-medium">{notificationResult.message}</span>
                        </div>
                        {notificationResult.output && (
                          <pre className="text-xs mt-2 whitespace-pre-wrap max-h-32 overflow-y-auto bg-white/50 p-2 rounded">
                            {notificationResult.output}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={loadExpiringItems}
                    variant="ghost"
                    size="sm"
                    className="w-full gap-2 text-gray-500"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh Data
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Database Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-gray-600" />
                System Information
              </CardTitle>
              <CardDescription>{t('adminUI.CompanyStatistics')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-gray-600 mb-1">Total Users</div>
                  <div className="text-xl font-bold">{stats?.totalUsers || 0}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-gray-600 mb-1">Active Workers</div>
                  <div className="text-xl font-bold">{stats?.activeWorkers || 0}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-gray-600 mb-1">Certificates</div>
                  <div className="text-xl font-bold">{stats?.certificates || 0}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-gray-600 mb-1">Completions</div>
                  <div className="text-xl font-bold">{stats?.completions || 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Company Training Management - Full Width Section */}
        <div className="mt-8">
          <CompanyTrainingManager />
        </div>

        {/* License Management Dialog - REMOVED */}

        {/* Cloud Storage Dialog */}
        <Dialog open={showCloudDialog} onOpenChange={setShowCloudDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.ConfigureCloudStorage')}</DialogTitle>
              <DialogDescription>{t('adminUI.SetUpCloudflareRForFileStorage')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveCloudStorage} className="space-y-4">
              <div>
                <Label>{t('adminUI.AccountId')}</Label>
                <Input
                  value={cloudForm.accountId}
                  onChange={(e) => setCloudForm({ ...cloudForm, accountId: e.target.value })}
                  placeholder="Your Cloudflare Account ID"
                  required
                />
              </div>
              <div>
                <Label>{t('adminUI.AccessKeyId')}</Label>
                <Input
                  value={cloudForm.accessKeyId}
                  onChange={(e) => setCloudForm({ ...cloudForm, accessKeyId: e.target.value })}
                  placeholder="R2 Access Key ID"
                  required
                />
              </div>
              <div>
                <Label>{t('adminUI.SecretAccessKey')}</Label>
                <Input
                  type="password"
                  value={cloudForm.secretAccessKey}
                  onChange={(e) => setCloudForm({ ...cloudForm, secretAccessKey: e.target.value })}
                  placeholder="R2 Secret Access Key"
                  required
                />
              </div>
              <div>
                <Label>{t('adminUI.BucketName')}</Label>
                <Input
                  value={cloudForm.bucketName}
                  onChange={(e) => setCloudForm({ ...cloudForm, bucketName: e.target.value })}
                  placeholder="Your R2 bucket name"
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCloudDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">{t('adminUI.SaveConfiguration')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminSettings;
